import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Wrench,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Plus,
  Filter,
  RefreshCw,
  Settings,
  Loader2,
  ChevronRight,
  Timer,
  IndianRupee,
  ClipboardList
} from 'lucide-react';
import api from '../services/api';

const MaintenancePlanner = () => {
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filters, setFilters] = useState({ status: '', priority: '', asset_type: '' });
  const [newSchedule, setNewSchedule] = useState({
    asset_id: '',
    maintenance_type: 'Preventive',
    title: '',
    description: '',
    priority: 'Medium',
    scheduled_date: '',
    due_date: '',
    estimated_duration_hours: 2,
    cost_estimate: 0,
    recurrence: 'None'
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [schedulesRes, statsRes, assetsRes] = await Promise.all([
        api.client.get('/maintenance/schedules', { params: filters }),
        api.client.get('/maintenance/stats'),
        api.client.get('/maintenance/assets')
      ]);
      setSchedules(schedulesRes.data.data || []);
      setStats(statsRes.data);
      setAssets(assetsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      await api.client.post('/maintenance/schedules', newSchedule);
      setShowCreateDialog(false);
      setNewSchedule({
        asset_id: '',
        maintenance_type: 'Preventive',
        title: '',
        description: '',
        priority: 'Medium',
        scheduled_date: '',
        due_date: '',
        estimated_duration_hours: 2,
        cost_estimate: 0,
        recurrence: 'None'
      });
      fetchData();
    } catch (error) {
      console.error('Error creating schedule:', error);
    }
  };

  const handleCompleteSchedule = async (scheduleId) => {
    try {
      await api.client.post(`/maintenance/schedules/${scheduleId}/complete`);
      fetchData();
    } catch (error) {
      console.error('Error completing schedule:', error);
    }
  };

  const handleUpdateStatus = async (scheduleId, status) => {
    try {
      await api.client.put(`/maintenance/schedules/${scheduleId}`, { status });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      High: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      Medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      Low: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return colors[priority] || colors.Medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      Scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      InProgress: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      Completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      Overdue: 'bg-red-500/20 text-red-400 border-red-500/30',
      Cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[status] || colors.Scheduled;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="maintenance-planner">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
            Preventive Maintenance Planner
          </h1>
          <p className="text-gray-400 mt-1">Schedule and track asset maintenance activities</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600" data-testid="create-schedule-btn">
              <Plus className="w-4 h-4 mr-2" /> Schedule Maintenance
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#12172e] border-cyan-500/30 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-cyan-400">Schedule New Maintenance</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-300">Asset</Label>
                <Select value={newSchedule.asset_id} onValueChange={(v) => setNewSchedule({...newSchedule, asset_id: v})}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700">
                    <SelectValue placeholder="Select asset" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {assets.map(asset => (
                      <SelectItem key={asset.id} value={asset.id}>{asset.name} ({asset.type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Title</Label>
                <Input 
                  className="bg-slate-800/50 border-slate-700"
                  value={newSchedule.title}
                  onChange={(e) => setNewSchedule({...newSchedule, title: e.target.value})}
                  placeholder="e.g., Quarterly pump inspection"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Type</Label>
                  <Select value={newSchedule.maintenance_type} onValueChange={(v) => setNewSchedule({...newSchedule, maintenance_type: v})}>
                    <SelectTrigger className="bg-slate-800/50 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="Preventive">Preventive</SelectItem>
                      <SelectItem value="Corrective">Corrective</SelectItem>
                      <SelectItem value="Emergency">Emergency</SelectItem>
                      <SelectItem value="Inspection">Inspection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Priority</Label>
                  <Select value={newSchedule.priority} onValueChange={(v) => setNewSchedule({...newSchedule, priority: v})}>
                    <SelectTrigger className="bg-slate-800/50 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Scheduled Date</Label>
                  <Input 
                    type="datetime-local"
                    className="bg-slate-800/50 border-slate-700"
                    value={newSchedule.scheduled_date}
                    onChange={(e) => setNewSchedule({...newSchedule, scheduled_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Due Date</Label>
                  <Input 
                    type="datetime-local"
                    className="bg-slate-800/50 border-slate-700"
                    value={newSchedule.due_date}
                    onChange={(e) => setNewSchedule({...newSchedule, due_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Est. Duration (hrs)</Label>
                  <Input 
                    type="number"
                    className="bg-slate-800/50 border-slate-700"
                    value={newSchedule.estimated_duration_hours}
                    onChange={(e) => setNewSchedule({...newSchedule, estimated_duration_hours: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Est. Cost (₹)</Label>
                  <Input 
                    type="number"
                    className="bg-slate-800/50 border-slate-700"
                    value={newSchedule.cost_estimate}
                    onChange={(e) => setNewSchedule({...newSchedule, cost_estimate: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Recurrence</Label>
                <Select value={newSchedule.recurrence} onValueChange={(v) => setNewSchedule({...newSchedule, recurrence: v})}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="None">One-time</SelectItem>
                    <SelectItem value="Weekly">Weekly</SelectItem>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Quarterly">Quarterly</SelectItem>
                    <SelectItem value="Yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Description</Label>
                <Input 
                  className="bg-slate-800/50 border-slate-700"
                  value={newSchedule.description}
                  onChange={(e) => setNewSchedule({...newSchedule, description: e.target.value})}
                  placeholder="Additional details..."
                />
              </div>
              <Button 
                onClick={handleCreateSchedule}
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500"
                disabled={!newSchedule.asset_id || !newSchedule.title || !newSchedule.scheduled_date}
              >
                Create Schedule
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-[#12172e]/80 border-cyan-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <ClipboardList className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                  <p className="text-xs text-gray-400">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#12172e]/80 border-cyan-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/20">
                  <Calendar className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.scheduled}</p>
                  <p className="text-xs text-gray-400">Scheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#12172e]/80 border-cyan-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/20">
                  <Timer className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.in_progress}</p>
                  <p className="text-xs text-gray-400">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#12172e]/80 border-cyan-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.completed}</p>
                  <p className="text-xs text-gray-400">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#12172e]/80 border-red-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
                  <p className="text-xs text-gray-400">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#12172e]/80 border-orange-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-400">{stats.critical_pending}</p>
                  <p className="text-xs text-gray-400">Critical</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#12172e]/80 border-cyan-500/20">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400 text-sm">Filters:</span>
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v === 'all' ? '' : v})}>
              <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
                <SelectItem value="InProgress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.priority} onValueChange={(v) => setFilters({...filters, priority: v === 'all' ? '' : v})}>
              <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.asset_type} onValueChange={(v) => setFilters({...filters, asset_type: v === 'all' ? '' : v})}>
              <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700">
                <SelectValue placeholder="Asset Type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Reservoir">Reservoir</SelectItem>
                <SelectItem value="WTP">WTP</SelectItem>
                <SelectItem value="OHT">OHT</SelectItem>
                <SelectItem value="PumpHouse">Pump House</SelectItem>
                <SelectItem value="Pipeline">Pipeline</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchData} className="border-cyan-500/30 text-cyan-400">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Schedules List */}
      <div className="grid gap-4">
        {loading ? (
          <Card className="bg-[#12172e]/80 border-cyan-500/20">
            <CardContent className="p-8 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </CardContent>
          </Card>
        ) : schedules.length === 0 ? (
          <Card className="bg-[#12172e]/80 border-cyan-500/20">
            <CardContent className="p-8 text-center">
              <Wrench className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No maintenance schedules found</p>
              <p className="text-gray-500 text-sm mt-2">Click "Schedule Maintenance" to create one</p>
            </CardContent>
          </Card>
        ) : (
          schedules.map((schedule) => (
            <Card key={schedule.id} className="bg-[#12172e]/80 border-cyan-500/20 hover:border-cyan-500/40 transition-colors" data-testid={`schedule-${schedule.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-cyan-500/20">
                        <Wrench className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{schedule.title}</h3>
                        <p className="text-sm text-gray-400">{schedule.asset_name} • {schedule.asset_type}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <Badge className={getPriorityColor(schedule.priority)}>{schedule.priority}</Badge>
                      <Badge className={getStatusColor(schedule.status)}>{schedule.status}</Badge>
                      <Badge variant="outline" className="border-slate-600 text-gray-400">
                        {schedule.maintenance_type}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> {formatDate(schedule.scheduled_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {schedule.estimated_duration_hours}h
                      </span>
                      {schedule.cost_estimate > 0 && (
                        <span className="flex items-center gap-1">
                          <IndianRupee className="w-4 h-4" /> {schedule.cost_estimate.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {schedule.status === 'Scheduled' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-cyan-500/30 text-cyan-400"
                        onClick={() => handleUpdateStatus(schedule.id, 'InProgress')}
                      >
                        Start
                      </Button>
                    )}
                    {schedule.status === 'InProgress' && (
                      <Button 
                        size="sm" 
                        className="bg-green-500/20 text-green-400 border border-green-500/30"
                        onClick={() => handleCompleteSchedule(schedule.id)}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Upcoming Maintenance Section */}
      {stats?.upcoming && stats.upcoming.length > 0 && (
        <Card className="bg-[#12172e]/80 border-cyan-500/20">
          <CardHeader>
            <CardTitle className="text-cyan-400 flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Upcoming This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.upcoming.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      item.priority === 'Critical' ? 'bg-red-500' :
                      item.priority === 'High' ? 'bg-orange-500' : 'bg-yellow-500'
                    }`} />
                    <div>
                      <p className="text-white font-medium">{item.title}</p>
                      <p className="text-sm text-gray-400">{item.asset_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-cyan-400">{formatDate(item.scheduled_date)}</p>
                    <Badge className={getPriorityColor(item.priority)} variant="outline">{item.priority}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MaintenancePlanner;
